import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';

export const SignIn = () => {
  const { signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSignIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Sign in failed", error);
      setError("Failed to sign in. Please try again.");
      if (error?.code === 'auth/popup-closed-by-user') {
        setError("Sign-in cancelled.");
      } else if (error?.code === 'auth/unauthorized-domain') {
        setError("This domain is not authorized for sign-in.");
      }
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 px-4 py-8">
      <SEO 
        title="Sign In"
        description="Sign in to Atelier Persopol to start creating your custom AI art masterpieces."
        url="https://www.atelier-persopol.com/signin"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-6 md:p-12 rounded-xl md:rounded-2xl shadow-xl text-center max-w-md w-full"
      >
        <h2 className="text-2xl md:text-3xl font-serif font-bold mb-3 md:mb-4">Welcome</h2>
        <p className="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">Sign up / Sign in to continue your creative journey.</p>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
            {error}
          </div>
        )}

        <button
          onClick={handleSignIn}
          className="w-full bg-black text-white py-4 md:py-4 rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center space-x-3 touch-manipulation min-h-[48px]"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>Continue with Google</span>
        </button>

        <p className="mt-6 text-xs text-gray-500">
          By continuing, you agree to our{' '}
          <a href="/privacy" className="underline hover:text-gray-800">
            Privacy Policy
          </a>
        </p>
      </motion.div>
    </div>
  );
};

